import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { useCompare } from "@/lib/useCompare";
import { useApp } from "@/context/AppContext";

const HERO_PAGES = ["/", "/living", "/invests", "/luxe"];

function navLinkTone(isActive, heroDark) {
  if (isActive) return heroDark ? "text-[#9ED3B8]" : "text-[var(--accent-gold)]";
  if (heroDark) return "text-white/90 hover:text-[#9ED3B8]";
  return "text-[var(--text-primary)] hover:text-[var(--accent-gold)]";
}

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/living", label: "Living" },
  { to: "/invests", label: "Invests" },
  { to: "/luxe", label: "Luxe" },
  { to: "/properties", label: "Properties" },
  { to: "/blog", label: "Blogs" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { ids } = useCompare();
  const { theme } = useApp();
  const location = useLocation();
  const heroDark = !scrolled && HERO_PAGES.includes(location.pathname);
  const logoOnDark = theme === "dark" || heroDark;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 z-[100] w-full transition-all duration-500 ${scrolled ? "glass border-b" : "bg-transparent border-b border-transparent"}`}
      style={scrolled ? {} : {}}
      data-testid="main-navbar"
    >
      <div className="container-arch flex h-[72px] items-center justify-between">
        <Logo onDark={logoOnDark} />

        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-sm font-medium transition-colors duration-300 ${navLinkTone(isActive, heroDark)}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          {ids.length > 0 && (
            <Link to="/compare" data-testid="nav-compare-badge" className="hidden items-center gap-1.5 rounded-full border border-[var(--accent-a50)] px-3 py-1.5 text-xs font-medium text-[var(--accent-gold)] sm:inline-flex">
              Compare · {ids.length}
            </Link>
          )}
          <ThemeToggle className={`hidden sm:inline-flex ${heroDark ? "!border-white/40 !text-white" : ""}`} />
          <Link to="/request" data-testid="nav-cta-requirement" className="btn-gold hidden !px-5 !py-2.5 text-xs md:inline-flex">
            Find My Property <ArrowUpRight className="h-4 w-4" />
          </Link>

          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu" className={`inline-flex h-9 w-9 items-center justify-center rounded-full border lg:hidden ${heroDark ? "border-white/40 text-white" : "border-[var(--border-subtle)] text-[var(--text-primary)]"}`}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="glass border-t lg:hidden" data-testid="mobile-menu">
          <div className="container-arch flex flex-col gap-1 py-5">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={({ isActive }) => `rounded-lg px-3 py-3 text-base font-medium ${isActive ? "text-[var(--accent-gold)]" : "text-[var(--text-primary)]"}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="my-2 hairline" />
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Link to="/contact" className="btn-outline-gold flex-1 !py-2.5 text-xs">Talk to Archedia</Link>
            </div>
            <Link to="/request" className="btn-gold mt-2 !py-3 text-sm">Find My Property</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
